import { cn, toParsablePhone } from "@/lib/utils";
import parsePhoneNumber from "libphonenumber-js/max";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CountryCodeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

export const COUNTRY_CODES = [
  { code: "+1", country: "US", iso: "US" },
  { code: "+44", country: "UK", iso: "GB" },
  { code: "+63", country: "PH", iso: "PH" },
  //   { code: '+91', country: 'IN' },
  // Add more country codes as needed
];

export const DEFAULT_COUNTRY_CODE = COUNTRY_CODES[0].code;

export function getPhoneParts(phone: string) {
  if (!phone.trim()) {
    return { countryCode: DEFAULT_COUNTRY_CODE, phoneNumber: "" };
  }

  const digits = phone.replace(/[^0-9]/g, "");
  const parsed = parsePhoneNumber(toParsablePhone(phone.trim()));

  if (parsed?.countryCallingCode && parsed.nationalNumber) {
    const byIso = COUNTRY_CODES.find((item) => item.iso === parsed.country);
    const byCallingCode = COUNTRY_CODES.find(
      (item) => item.code === `+${parsed.countryCallingCode}`,
    );

    return {
      countryCode: (byIso ?? byCallingCode)?.code ?? DEFAULT_COUNTRY_CODE,
      phoneNumber: parsed.nationalNumber,
    };
  }

  return { countryCode: DEFAULT_COUNTRY_CODE, phoneNumber: digits };
}

export function isValidPhoneForSelectedCountry(
  phone: string,
  countryCode: string,
): boolean {
  const selected = COUNTRY_CODES.find((item) => item.code === countryCode);
  const parsed = parsePhoneNumber(phone);
  return parsed?.isValid() === true && parsed.country === selected?.iso;
}

export function CountryCodeSelector({
  value,
  onChange,
  className,
  disabled = false,
}: CountryCodeSelectorProps) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className={cn(
          "h-12 w-[140px] rounded-[12px] border-[#e3dbcf] bg-[#faf4ea] text-sm text-black focus-visible:ring-2 focus-visible:ring-black/5",
          className,
        )}
      >
        <SelectValue placeholder="Code" />
      </SelectTrigger>
      <SelectContent>
        {COUNTRY_CODES.map((item) => (
          <SelectItem key={item.iso} value={item.code}>
            {item.code} ({item.country})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}